import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Chapter } from "./Chapter";
import { Reveal } from "./Reveal";
import { contact, socials, hours } from "../data/siteConfig";
import { socialPendingToast } from "../lib/notify";
import {
    Phone,
    MessageSquareText,
    Mail,
    MapPin,
    Navigation,
    ArrowRight,
    Loader2,
} from "lucide-react";
import { FacebookIcon, InstagramIcon, TikTokIcon } from "./SocialIcons";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIAL_ICONS = { facebook: FacebookIcon, instagram: InstagramIcon, tiktok: TikTokIcon };

const ContactMethod = ({ icon: Icon, label, value, href, testid }) => (
    <a
        href={href}
        className="group flex items-center gap-4 rounded-2xl border border-stone-200 bg-white p-5 text-left shadow-sm transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:border-stone-400 hover:shadow-md"
        data-testid={testid}
    >
        <Icon
            className="h-6 w-6 shrink-0 text-fern transition-transform duration-300 group-hover:scale-110"
            aria-hidden="true"
        />
        <span>
            <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">
                {label}
            </span>
            <span className="mt-0.5 block text-sm font-semibold text-[#1c1917] sm:text-base">
                {value}
            </span>
        </span>
    </a>
);
const EMPTY_FORM = {
    catName: "",
    surname: "",
    email: "",
    arrival: "",
    departure: "",
    notes: "",
    fullName: "",
    address: "",
    phone: "",
    vet: "",
    vaccinationConfirmed: false,
    defleaConfirmed: false,
    website: "", // honeypot — must stay empty; bots fill it
};

export const Contact = () => {
    const [kind, setKind] = useState(null); // null | "new" | "returning"
    const [form, setForm] = useState(EMPTY_FORM);
    const [sending, setSending] = useState(false);

    const set = (k) => (e) =>
        setForm({
            ...form,
            [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
        });

    const submit = async (e) => {
        e.preventDefault();
        if (!kind) return;
        if (
            !form.catName.trim() ||
            !form.surname.trim() ||
            !form.email.trim() ||
            !form.arrival ||
            !form.departure
        ) {
            toast.error(
                "Please complete your cat's name, your surname, your email address and both dates.",
            );
            return;
        }
        if (kind === "new") {
            if (
                !form.fullName.trim() ||
                !form.address.trim() ||
                !form.phone.trim() ||
                !form.vet.trim()
            ) {
                toast.error(
                    "Please complete your full name, address, telephone number and vet clinic.",
                );
                return;
            }
            if (!form.vaccinationConfirmed || !form.defleaConfirmed) {
                toast.error(
                    "Please tick both confirmation boxes — vaccination information and de-fleaing.",
                );
                return;
            }
        }
        setSending(true);
        try {
            await axios.post(`${API}/enquiries`, { ...form, kind });
            toast.success("Thank you. We've received your booking request.", {
                description:
                    "We'll reply personally by email to confirm your booking.",
            });
            setForm(EMPTY_FORM);
        } catch {
            toast.error("Something went wrong sending your request.", {
                description: "Please try again in a moment.",
            });
        } finally {
            setSending(false);
        }
    };

    const methods = [
        {
            icon: Phone,
            label: "Call — landline",
            value: contact.landline.display,
            href: `tel:${contact.landline.href}`,
            show: !contact.landline.pending && !!contact.landline.href,
            testid: "contact-call-landline",
        },
        {
            icon: Phone,
            label: "Call — mobile",
            value: contact.mobile.display,
            href: `tel:${contact.mobile.href}`,
            show: !contact.mobile.pending && !!contact.mobile.href,
            testid: "contact-call-mobile",
        },
        {
            icon: MessageSquareText,
            label: "Text — mobile",
            value: contact.mobile.display,
            href: `sms:${contact.mobile.href}`,
            show: !contact.mobile.pending && !!contact.mobile.href,
            testid: "contact-text-mobile",
        },
        {
            icon: Mail,
            label: "Email / message",
            value: contact.email.display,
            href: `mailto:${contact.email.href}`,
            show: !contact.email.pending && !!contact.email.href,
            testid: "contact-email",
        },
    ].filter((m) => m.show);

    const mapEmbed = `https://www.google.com/maps?q=${encodeURIComponent(contact.mapQuery)}&output=embed`;

    const inputCls =
        "mt-1.5 w-full rounded-xl border border-stone-300 bg-cream px-4 py-3.5 text-base text-[#1c1917] placeholder:text-stone-400";
    const labelCls =
        "font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500";

    return (
        <section
            className="bg-white py-24 text-[#1c1917] sm:py-32"
            data-testid="contact-section"
        >
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <Chapter
                    no="07"
                    id="book"
                    eyebrow="Contact & Book"
                    title="We'd love to welcome your cat"
                    intro="Planning a stay? Send us your cat's details and preferred dates below. We'll let you know about availability."
                />

                <div className="mt-16 grid gap-12 lg:grid-cols-12">
                    {/* Contact methods + booking request */}
                    <div className="space-y-4 lg:col-span-5">
                        {methods.length > 0 && (
                            <Reveal className="hidden lg:block">
                                <div className="grid gap-4 lg:grid-cols-1 xl:grid-cols-2">
                                    {methods.map((m) => (
                                        <ContactMethod key={m.testid} {...m} />
                                    ))}
                                </div>
                            </Reveal>
                        )}

                        <Reveal delay={0.1}>
                            <div
                                id="enquiry-form"
                                className="scroll-mt-28 rounded-3xl border border-stone-200 bg-white p-7 text-[#1c1917] shadow-sm sm:p-9"
                                data-testid="enquiry-form"
                            >
                                <h3 className="font-serif text-2xl font-semibold">
                                    Booking Request
                                </h3>
                                <p className="mt-2 rounded-xl bg-fernlight p-4 text-sm leading-relaxed text-stone-700">
                                    Your booking request will be sent directly
                                    to Mākara Cattery. We'll reply personally
                                    by email to confirm your booking.
                                </p>

                                {/* Two clear booking paths */}
                                <div
                                    className="mt-6 grid gap-4 sm:grid-cols-2"
                                    data-testid="booking-type-selector"
                                >
                                    {[
                                        {
                                            id: "new",
                                            title: "New customer",
                                            desc: "First time staying with us — we'll ask for a few extra details.",
                                        },
                                        {
                                            id: "returning",
                                            title: "Returning customer",
                                            desc: "Welcome back — just your cat, dates and email.",
                                        },
                                    ].map((o) => (
                                        <button
                                            key={o.id}
                                            type="button"
                                            onClick={() => setKind(o.id)}
                                            data-testid={`booking-type-${o.id}`}
                                            aria-pressed={kind === o.id}
                                            className={`rounded-3xl border-2 p-6 text-left transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 sm:p-7 ${
                                                kind === o.id
                                                    ? "border-fern bg-fernlight"
                                                    : "border-stone-200 bg-cream hover:border-stone-400"
                                            }`}
                                        >
                                            <span className="block font-serif text-2xl font-semibold">
                                                {o.title}
                                            </span>
                                            <span className="mt-1.5 block text-sm leading-relaxed text-stone-600">
                                                {o.desc}
                                            </span>
                                        </button>
                                    ))}
                                </div>

                                {kind && (
                                    <form onSubmit={submit} className="mt-8 space-y-4" data-testid="booking-form">
                                        {/* Honeypot — visually hidden; only spam bots fill this. */}
                                        <div className="absolute -left-[9999px] -top-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
                                            <label>
                                                Website
                                                <input
                                                    type="text"
                                                    name="website"
                                                    value={form.website}
                                                    onChange={set("website")}
                                                    tabIndex={-1}
                                                    autoComplete="off"
                                                    data-testid="enquiry-honeypot"
                                                />
                                            </label>
                                        </div>
                                        {kind === "new" && (
                                            <>
                                                <p className={`${labelCls} pt-2`}>
                                                    Your details
                                                </p>
                                                <label className="block">
                                                    <span className={labelCls}>Full name *</span>
                                                    <input
                                                        type="text"
                                                        value={form.fullName}
                                                        onChange={set("fullName")}
                                                        required
                                                        data-testid="enquiry-fullname-input"
                                                        className={inputCls}
                                                        placeholder="Jane Smith"
                                                    />
                                                </label>
                                                <label className="block">
                                                    <span className={labelCls}>Address *</span>
                                                    <input
                                                        type="text"
                                                        value={form.address}
                                                        onChange={set("address")}
                                                        required
                                                        data-testid="enquiry-address-input"
                                                        className={inputCls}
                                                        placeholder="Street, suburb, city"
                                                    />
                                                </label>
                                                <div className="grid gap-4 sm:grid-cols-2">
                                                    <label className="block">
                                                        <span className={labelCls}>Telephone number *</span>
                                                        <input
                                                            type="tel"
                                                            value={form.phone}
                                                            onChange={set("phone")}
                                                            required
                                                            data-testid="enquiry-phone-input"
                                                            className={inputCls}
                                                            placeholder="04 ... or 02 ..."
                                                        />
                                                    </label>
                                                    <label className="block">
                                                        <span className={labelCls}>Email address *</span>
                                                        <input
                                                            type="email"
                                                            value={form.email}
                                                            onChange={set("email")}
                                                            required
                                                            data-testid="enquiry-email-input"
                                                            className={inputCls}
                                                            placeholder="you@example.com"
                                                        />
                                                    </label>
                                                </div>
                                                <label className="block">
                                                    <span className={labelCls}>
                                                        Veterinary clinic your cat is registered with *
                                                    </span>
                                                    <input
                                                        type="text"
                                                        value={form.vet}
                                                        onChange={set("vet")}
                                                        required
                                                        data-testid="enquiry-vet-input"
                                                        className={inputCls}
                                                        placeholder="e.g. Karori Veterinary Clinic"
                                                    />
                                                </label>
                                                <div className="space-y-3 pt-1">
                                                    <label
                                                        className="flex cursor-pointer items-start gap-3 rounded-xl border border-stone-200 bg-cream p-4"
                                                        data-testid="enquiry-vaccination-label"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={form.vaccinationConfirmed}
                                                            onChange={set("vaccinationConfirmed")}
                                                            className="mt-0.5 h-5 w-5 shrink-0 accent-[#7C8966]"
                                                            data-testid="enquiry-vaccination-checkbox"
                                                        />
                                                        <span className="text-base leading-relaxed text-stone-700">
                                                            I will bring my cat's vaccination
                                                            information with me on their first visit.
                                                        </span>
                                                    </label>
                                                    <label
                                                        className="flex cursor-pointer items-start gap-3 rounded-xl border border-stone-200 bg-cream p-4"
                                                        data-testid="enquiry-deflea-label"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={form.defleaConfirmed}
                                                            onChange={set("defleaConfirmed")}
                                                            className="mt-0.5 h-5 w-5 shrink-0 accent-[#7C8966]"
                                                            data-testid="enquiry-deflea-checkbox"
                                                        />
                                                        <span className="text-base leading-relaxed text-stone-700">
                                                            I confirm that my cat will be de-fleaed
                                                            before their stay.
                                                        </span>
                                                    </label>
                                                </div>
                                                <p className={`${labelCls} pt-2`}>
                                                    Your cat &amp; dates
                                                </p>
                                            </>
                                        )}

                                        <div className="grid gap-4 sm:grid-cols-2">
                                            <label className="block">
                                                <span className={labelCls}>Cat's name *</span>
                                                <input
                                                    type="text"
                                                    value={form.catName}
                                                    onChange={set("catName")}
                                                    required
                                                    data-testid="enquiry-catname-input"
                                                    className={inputCls}
                                                    placeholder="Mittens"
                                                />
                                            </label>
                                            <label className="block">
                                                <span className={labelCls}>Surname / family name *</span>
                                                <input
                                                    type="text"
                                                    value={form.surname}
                                                    onChange={set("surname")}
                                                    required
                                                    data-testid="enquiry-surname-input"
                                                    className={inputCls}
                                                    placeholder="Smith"
                                                />
                                            </label>
                                        </div>
                                        {kind === "returning" && (
                                            <label className="block">
                                                <span className={labelCls}>Email address *</span>
                                                <input
                                                    type="email"
                                                    value={form.email}
                                                    onChange={set("email")}
                                                    required
                                                    data-testid="enquiry-email-input"
                                                    className={inputCls}
                                                    placeholder="you@example.com"
                                                />
                                            </label>
                                        )}
                                        <div className="grid gap-4 sm:grid-cols-2">
                                            <label className="block">
                                                <span className={labelCls}>Date in *</span>
                                                <input
                                                    type="date"
                                                    value={form.arrival}
                                                    onChange={set("arrival")}
                                                    required
                                                    data-testid="enquiry-arrival-input"
                                                    className={inputCls}
                                                />
                                            </label>
                                            <label className="block">
                                                <span className={labelCls}>Date out *</span>
                                                <input
                                                    type="date"
                                                    value={form.departure}
                                                    onChange={set("departure")}
                                                    required
                                                    data-testid="enquiry-departure-input"
                                                    className={inputCls}
                                                />
                                            </label>
                                        </div>
                                        <label className="block">
                                            <span className={labelCls}>Notes (optional)</span>
                                            <textarea
                                                value={form.notes}
                                                onChange={set("notes")}
                                                rows={4}
                                                data-testid="enquiry-notes-input"
                                                className={`${inputCls} resize-none`}
                                                placeholder="Anything you'd like us to know — personality, medication, routines."
                                            />
                                        </label>
                                        <button
                                            type="submit"
                                            disabled={sending}
                                            data-testid="enquiry-submit-button"
                                            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#1c1917] px-8 py-4 text-lg font-semibold text-cream shadow-lg shadow-black/10 transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-black disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            {sending ? (
                                                <Loader2 className="h-5 w-5 animate-spin" />
                                            ) : (
                                                <>
                                                    Send booking request
                                                    <ArrowRight className="h-4 w-4" />
                                                </>
                                            )}
                                        </button>
                                        <p className="text-center text-sm leading-relaxed text-stone-500">
                                            This is a booking request, not an
                                            instant confirmation — we'll reply
                                            personally by email.
                                        </p>
                                        <p className="text-center text-xs leading-relaxed text-stone-500">
                                            Bookings are subject to our{" "}
                                            <a
                                                href="#/terms-and-conditions"
                                                data-testid="booking-terms-link"
                                                className="text-fern underline underline-offset-4 transition-colors duration-200 hover:text-[#1c1917]"
                                            >
                                                Terms &amp; Conditions
                                            </a>
                                            .
                                        </p>
                                    </form>
                                )}
                            </div>
                        </Reveal>
                    </div>

                    {/* Map & directions */}
                    <div className="lg:col-span-7">
                        <Reveal delay={0.05}>
                            <div
                                className="spotlight-frame overflow-hidden rounded-3xl border border-stone-300 shadow-xl shadow-stone-900/10"
                                data-testid="map-embed"
                            >
                                <iframe
                                    title="Map — Mākara Village Boarding Cattery, 384 Mākara Road, Mākara, Wellington"
                                    src={mapEmbed}
                                    className="h-80 w-full sm:h-96"
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                    allowFullScreen
                                />
                            </div>
                        </Reveal>

                        <Reveal delay={0.12}>
                            <div className="mt-6 grid gap-5 sm:grid-cols-2">
                                <div
                                    className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm"
                                    data-testid="address-card"
                                >
                                    <MapPin
                                        className="h-6 w-6 text-stone-600"
                                        aria-hidden="true"
                                    />
                                    <address className="mt-3 text-sm not-italic leading-relaxed text-stone-700 sm:text-base">
                                        <strong className="text-[#1c1917]">
                                            Mākara Village Boarding Cattery
                                        </strong>
                                        <br />
                                        {contact.address.line1}
                                        <br />
                                        {contact.address.line2}
                                        <br />
                                        {contact.address.country}
                                    </address>
                                    <p className="mt-3 text-xs leading-relaxed text-stone-500">
                                        {contact.address.note}
                                    </p>
                                    <div className="mt-4 flex flex-wrap gap-3">
                                        <a
                                            href={contact.directionsUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 rounded-full bg-obsidian px-5 py-2.5 text-xs font-semibold text-cream transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-ember"
                                            data-testid="get-directions-button"
                                        >
                                            <Navigation className="h-3.5 w-3.5" />
                                            Get Directions
                                        </a>
                                        <a
                                            href={contact.mapsUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 rounded-full border border-stone-300 px-5 py-2.5 text-xs font-semibold text-stone-700 transition-[border-color,color,transform] duration-300 hover:-translate-y-0.5 hover:border-obsidian"
                                            data-testid="open-in-maps-button"
                                        >
                                            Open in Maps
                                        </a>
                                    </div>
                                </div>

                                <div
                                    className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm"
                                    data-testid="arrival-directions-card"
                                >
                                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">
                                        When you arrive
                                    </p>
                                    <ol className="mt-3 space-y-2.5 text-sm leading-relaxed text-stone-700">
                                        <li className="flex gap-3">
                                            <span className="font-mono text-stone-500">1</span>
                                            Coming from Karori, follow Mākara
                                            Road into Mākara village.
                                        </li>
                                        <li className="flex gap-3">
                                            <span className="font-mono text-stone-500">2</span>
                                            Turn into the property driveway and
                                            continue past the house toward the
                                            rear.
                                        </li>
                                        <li className="flex gap-3">
                                            <span className="font-mono text-stone-500">3</span>
                                            You'll find the cattery at the back,
                                            with parking and turning space for
                                            about two cars.
                                        </li>
                                    </ol>
                                </div>
                            </div>
                        </Reveal>

                        {/* Socials */}
                        <Reveal delay={0.18}>
                            <div
                                className="mt-6 rounded-2xl bg-obsidian p-6 text-cream"
                                data-testid="social-links-card"
                            >
                                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-cream/50">
                                    Follow the cats — photos & videos most days
                                </p>
                                <div className="mt-4 flex flex-wrap gap-3">
                                    {socials.map((s) => {
                                        const Icon = SOCIAL_ICONS[s.id];
                                        const cls =
                                            "inline-flex items-center gap-2.5 rounded-full border border-white/15 px-5 py-2.5 text-sm font-medium text-cream transition-[border-color,background-color,transform] duration-300 hover:-translate-y-0.5 hover:border-cream/40 hover:bg-ember";
                                        return s.url ? (
                                            <a
                                                key={s.id}
                                                href={s.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={cls}
                                                data-testid={`social-${s.id}`}
                                                aria-label={`${s.label} — ${s.account}`}
                                            >
                                                <Icon className="h-4 w-4 text-cream" />
                                                {s.account}
                                            </a>
                                        ) : (
                                            <button
                                                key={s.id}
                                                onClick={() => socialPendingToast(s.label, s.account)}
                                                className={cls}
                                                data-testid={`social-${s.id}`}
                                                aria-label={`${s.label} — ${s.account}`}
                                            >
                                                <Icon className="h-4 w-4 text-cream" />
                                                {s.account}
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="mt-4 text-xs text-stone-400">
                                    Open {hours.days.toLowerCase()} ·{" "}
                                    {hours.morning} & {hours.afternoon}
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
};
